import { IGetValueArgs } from "@components/smart/FieldInfo";
import { createPath } from "@odata/BindingContext";
import {
    EntitySetName,
    EntityTypeName,
    PrCSSZSubmissionCaregivingDetailEntity,
    PrCSSZSubmissionDetailEntity,
    PrCSSZSubmissionPaternityLeaveDetailEntity,
    PrCSSZSubmissionPaternityLeaveEntity,
    PrCSSZSubmissionPaymentBasisPaternityLeaveEntity,
    PrPaternityLeaveEntitlementEntity,
    PrWorkedInDaysIntervalEntity
} from "@odata/GeneratedEntityTypes";
import { PrPaternityLeaveEntitlementCode, PrSubmissionSubtypeCode, PrSubmissionTypeCode } from "@odata/GeneratedEnums";
import { setDefByEntityType } from "@pages/getDefByEntityType";
import { IDefinition, IGetDefinition } from "@pages/PageUtils";
import { paternityLeaveEntitlementTransformation } from "@pages/payroll/socialEvents/eventTypes/PaternityLeaveDef";
import { addCareReceiverDefinitions } from "@pages/payroll/socialEvents/SocialEventsDef";
import {
    getDefinitions as commonDef,
    getSubmissionFormDef,
    IGetSubmissionDefArgs,
    PrSubmissionGroup
} from "@pages/payroll/submission/Submission.def";
import {
    addBasicBenefitRequestGroup,
    addCommonNEMPRIDefinitions,
    addCSSZSwitchDef,
    addReturnToWorkFields,
    CSSZTranslationFiles,
    getConditionalFastEntryListRenderer,
    PrCSSZSubmissionEntity,
    WorkedInDaysLocalPath
} from "@pages/payroll/submission/submissionTypes/PrCSSZSubmission.utils";
import PrCSSZSubmissionPaternityLeaveFormView
    from "@pages/payroll/submission/submissionTypes/PrCSSZSubmissionPaternityLeaveFormView";
import i18next from "i18next";
import { cloneDeep } from "lodash";

import { IAppContext } from "../../../../contexts/appContext/AppContext.types";
import { BasicInputSizes, FieldType } from "../../../../enums";
import { addFormDef, addFormGroup } from "../../../../views/formView/Form.utils";

const _detailPath = (path: PrCSSZSubmissionPaternityLeaveDetailEntity, ...restProps: string[]) => createPath(PrCSSZSubmissionPaternityLeaveEntity.Detail, path, ...restProps);
const _paymentBasisPath = (path: PrCSSZSubmissionPaymentBasisPaternityLeaveEntity, ...restProps: string[]) =>
    _detailPath(PrCSSZSubmissionPaternityLeaveDetailEntity.PaymentBasis, path, ...restProps);

export const getDefinitions: IGetDefinition = (context: IAppContext, args?: IGetSubmissionDefArgs): IDefinition => {
    const d = cloneDeep(getSubmissionFormDef(PrCSSZSubmissionPaternityLeaveFormView, context, {
        ...args,
        type: PrSubmissionTypeCode.EmployersNotificationOfAnEmployeesBenefitApplication,
        subType: PrSubmissionSubtypeCode.NempriPaternityLeave
    }));

    // shared definitions with maternity leave
    addCommonNEMPRIDefinitions(d, context, args);

    // Maternity and Paternity leave has conditionally required decision number,
    // but we don't want to define the condition in the form logic -> let it on the user
    d.form.fieldDefinition[createPath(PrCSSZSubmissionEntity.Detail, PrCSSZSubmissionDetailEntity.DecisionNumber)].isRequired = false;

    // **************** BenefitRequest ****************
    addFormDef(d, _detailPath(PrCSSZSubmissionPaternityLeaveDetailEntity.DateStart), {
        label: i18next.t("Submission:SocialEventsForm.DateEventStart"),
    });
    addFormDef(d, _detailPath(PrCSSZSubmissionPaternityLeaveDetailEntity.PaternityReason), {
        type: FieldType.ComboBox,
        label: i18next.t("Submission:SocialEventsForm.Reason"),
        width: BasicInputSizes.XXL,
        defaultValue: PrPaternityLeaveEntitlementCode.Father,
        fieldSettings: {
            displayName: PrPaternityLeaveEntitlementEntity.Name,
            transformFetchedItems: paternityLeaveEntitlementTransformation
        },
        affectedFields: []
    });
    addBasicBenefitRequestGroup(d, [
        [{ id: _detailPath(PrCSSZSubmissionPaternityLeaveDetailEntity.DateStart) }],
        [{ id: _detailPath(PrCSSZSubmissionPaternityLeaveDetailEntity.PaternityReason) }]
    ]);

    // **************** CareReceiver definition ****************
    const careReceiverRows = addCareReceiverDefinitions(d, _detailPath(PrCSSZSubmissionPaternityLeaveDetailEntity.CareReceiver));
    addFormGroup(d, {
        id: PrSubmissionGroup.CareReceiver,
        title: i18next.t("Submission:SocialEventsForm.Child"),
        showGroupDivider: false,
        rows: careReceiverRows
    }, PrSubmissionGroup.BenefitRequest);

    // **************** Benefit Payment Inputs group ****************
    addCSSZSwitchDef(d, _paymentBasisPath(PrCSSZSubmissionPaymentBasisPaternityLeaveEntity.HasPlannedShifts), "HasPlannedShifts", [
        _paymentBasisPath(PrCSSZSubmissionPaymentBasisPaternityLeaveEntity.HasWorkedOnPlannedShifts)
    ]);
    addCSSZSwitchDef(d, _paymentBasisPath(PrCSSZSubmissionPaymentBasisPaternityLeaveEntity.HasWorkedOnPlannedShifts), "HasWorkedOnPlannedShifts", {
        isVisible: ({ storage }: IGetValueArgs) =>
            !!storage.getValueByPath(_paymentBasisPath(PrCSSZSubmissionPaymentBasisPaternityLeaveEntity.HasPlannedShifts))
    });
    // line items with dates...
    addFormDef(d, _paymentBasisPath(PrCSSZSubmissionPaymentBasisPaternityLeaveEntity.WorkedInDays, PrWorkedInDaysIntervalEntity.DateFrom), {
        label: i18next.t("Submission:SocialEventsForm.From"),
    });
    addFormDef(d, _paymentBasisPath(PrCSSZSubmissionPaymentBasisPaternityLeaveEntity.WorkedInDays, PrWorkedInDaysIntervalEntity.DateTo), {
        label: i18next.t("Submission:SocialEventsForm.To"),
    });
    addCSSZSwitchDef(d, WorkedInDaysLocalPath, "EmployeeWorkedInDays");

    addFormGroup(d, {
        id: PrSubmissionGroup.BenefitPaymentInputs,
        title: i18next.t("Submission:SocialEventsForm.BenefitPaymentInputs"),
        isSmallBottomMargin: true,
        rows: [
            [{ id: _paymentBasisPath(PrCSSZSubmissionPaymentBasisPaternityLeaveEntity.HasPlannedShifts) }],
            [{ id: _paymentBasisPath(PrCSSZSubmissionPaymentBasisPaternityLeaveEntity.HasWorkedOnPlannedShifts) }],
            [{ id: WorkedInDaysLocalPath }],
        ],
        lineItems: {
            collection: _paymentBasisPath(PrCSSZSubmissionPaymentBasisPaternityLeaveEntity.WorkedInDays),
            order: PrWorkedInDaysIntervalEntity.Id,
            canReorder: false,
            isItemCloneable: false,
            isTheOnlyItemRemovable: false,
            canAdd: (args) => !!args.storage.getValueByPath(WorkedInDaysLocalPath),
            columns: [
                { id: PrWorkedInDaysIntervalEntity.DateFrom },
                { id: PrWorkedInDaysIntervalEntity.DateTo }
            ],
            render: getConditionalFastEntryListRenderer(WorkedInDaysLocalPath)
        }
    }, PrSubmissionGroup.CareReceiver);

    // **************** Benefit Payment Inputs - return to work ****************
    addReturnToWorkFields(d, PrCSSZSubmissionCaregivingDetailEntity, { addDateReturnToWork: true });

    return {
        ...d,
        entitySet: EntitySetName.PrCSSZSubmissionsPaternityLeave
    };
};

getDefinitions.translationFiles = [
    ...commonDef.translationFiles,
    ...CSSZTranslationFiles,
];
setDefByEntityType(EntityTypeName.PrCSSZSubmissionPaternityLeave, getDefinitions);